import { Profile } from "../types/types";

export interface SocialLink {
    type: string;
    url: string;
}

export interface YoutubeItem {
    ytLink: string;
    shortTitle: string;
    shortDescription: string;
}

export interface FightItem {
    opponentName: string;
    date: string;
    location: string;
    result: string;
    round: number;
    typeOfEnding: string;
    organizationName?: string;
    preferredRuleSet: string;
}

export interface SponsorItem {
    id?: number;
    name: string;
    description: string;
    logo: string;
    link: string;
    /** Only present once the backend returns it (see BACKEND_CHANGES.md #4) */
    createdAt?: string | null;
}

/** Fields edited through the "Save change" buttons (one group per Figma section) */
export interface ProfileForm {
    bio: string;
    firstName: string;
    lastName: string;
    gender: string;
    nickname: string;
    dateOfBirth: string;
    organizationName: string;
    nationality: string;
    residenceCountry: string;
    trainingDisciplines: string[];
    weightClasses: string[];
    height: string;
    heightUnit: string;
    weight: string;
    weightUnit: string;
    reach: string;
    reachUnit: string;
    stance: string;
    titleHolder: boolean;
    team: string;
    gym: string;
    fightStyleDescription: string;
}

/** Fields that are persisted immediately (Add / Remove / Update buttons) */
export interface ProfileLists {
    socialLinks: SocialLink[];
    achievements: string[];
    fights: FightItem[];
    ytLinks: YoutubeItem[];
    sponsors: SponsorItem[];
}

export type Snapshot = ProfileForm & ProfileLists;

export type SectionKey = "bio" | "personal" | "location" | "fighterBasic" | "fighterAdditional";

export type FormErrors = Record<string, string>;

export type MeasureKey = "height" | "weight" | "reach";
export type UnitKey = "heightUnit" | "weightUnit" | "reachUnit";

export interface DeletionState {
    scheduledAt: string | null;
    busy: boolean;
    request: () => Promise<void>;
    cancel: () => Promise<void>;
}

/** Buttons shown in the page's top bar (Cancel + Save changes) */
export interface ProfileTopBarActions {
    /** true while at least one field differs from the saved profile */
    hasChanges: boolean;
    saving: boolean;
    save: () => void;
    cancel: () => void;
}

export type UpdateProfileUIProps = {
    member: Profile;
    isFighter: boolean;
    isManager: boolean;
    isOrganization: boolean;

    // section forms (saved together from the top bar)
    form: ProfileForm;
    setField: <K extends keyof ProfileForm>(name: K, value: ProfileForm[K]) => void;
    errors: FormErrors;
    clearError: (key: string) => void;
    isDirty: (section: SectionKey) => boolean;

    // dropdown data (all from backend)
    countries: string[];
    disciplineOptions: string[];
    weightClassOptions: string[];

    // list sections (saved immediately; resolve true on success)
    isPersisting: boolean;
    socialLinks: SocialLink[];
    commitSocialLinks: (next: SocialLink[]) => Promise<boolean>;
    achievements: string[];
    commitAchievements: (next: string[]) => Promise<boolean>;
    fights: FightItem[];
    commitFights: (next: FightItem[]) => Promise<boolean>;
    ytLinks: YoutubeItem[];
    commitYtLinks: (next: YoutubeItem[]) => Promise<boolean>;
    sponsors: SponsorItem[];
    commitSponsors: (next: SponsorItem[]) => Promise<boolean>;

    // avatar
    imageUrl: string;
    uploadingImage: boolean;
    onPickImage: (file: File) => void;

    // delete account
    deletion: DeletionState;
};

/** Allowed value ranges per unit (same limits the backend/signup flow use) */
export const MEASURE_RANGES: Record<MeasureKey, Record<string, number[]>> = {
    height: { cm: [100, 250], ft: [3, 8], inch: [36, 96] },
    weight: { kgs: [30, 200], lbs: [66, 440] },
    reach: { cm: [100, 250], ft: [3, 8], inch: [36, 100] },
};

/** Backend contract: SocialMediaAccountDetailsDto @Pattern */
export const ALLOWED_PLATFORMS = ["YOUTUBE", "FACEBOOK", "TWITTER", "INSTAGRAM", "LINKEDIN"];
